import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Card, Form } from 'react-bootstrap';
import { newsApi } from '../services/api';
import { News } from '../types';
import { getNewsImage, getNewsTitle, getNewsCategoryName, formatNewsDateShort } from '../services/newsHelpers';
import PageMeta from '../components/PageMeta';
import '../styles/pages.css';
import '../styles/NewsSection.css';

const PAGE_SIZE = 20;

const NewsListing: React.FC = () => {
  const [allNews, setAllNews] = useState<News[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchNews = async () => {
      setLoading(true);
      try {
        const data = await newsApi.getLatest(24);
        setAllNews(data);
      } catch (error) {
        console.error('Error loading news:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, []);

  // Filtra por busca
  const filtered = searchQuery
    ? allNews.filter(n =>
        n.titulo.toLowerCase().includes(searchQuery.toLowerCase()) ||
        n.subtitulo?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : allNews;

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const goToPage = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Gera a lista de páginas a exibir (com reticências)
  const getPageNumbers = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages: (number | '...')[] = [1];
    if (currentPage > 3) pages.push('...');
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      pages.push(i);
    }
    if (currentPage < totalPages - 2) pages.push('...');
    pages.push(totalPages);
    return pages;
  };

  return (
    <div className="page-container">
      <PageMeta
        title="Notícias"
        description="Acompanhe as últimas notícias do agronegócio da Campanha Gaúcha. Informação de qualidade para o produtor rural de Bagé e região."
        url="/noticias"
      />
      <div className="page-header">
        <div className="page-header-bg" style={{ backgroundImage: "url('/img/hero-image.jpg')" }} />
        <div className="page-header-overlay" />
        <Container className="page-header-content">
          <span className="section-badge section-badge--solid">Notícias do Agro</span>
          <h1>Informação Agricampanha</h1>
        </Container>
        <div className="page-header-fita" style={{ backgroundImage: "url('/img/bg-fita.png')" }} />
      </div>

      <Container className="page-content news-listing-container">
        {/* Search + results count */}
        <Row className="align-items-center mb-4">
          <Col lg={6}>
            <p className="news-results-count">
              {loading
                ? 'Carregando...'
                : searchQuery
                ? `${filtered.length} resultado${filtered.length !== 1 ? 's' : ''} para "${searchQuery}"`
                : `${filtered.length} notícia${filtered.length !== 1 ? 's' : ''} encontrada${filtered.length !== 1 ? 's' : ''}`}
            </p>
          </Col>
          <Col lg={6}>
            <Form onSubmit={handleSearch} className="news-search-form">
              <div className="news-search-wrapper">
                <input
                  type="search"
                  className="news-search-input"
                  placeholder="Pesquisar notícias..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  aria-label="Pesquisar notícias"
                />
                <button type="submit" className="news-search-btn">
                  <i className="bi bi-search"></i>
                </button>
              </div>
            </Form>
          </Col>
        </Row>

        {loading ? (
          <Row className="g-4">
            {[1,2,3,4,5,6,7,8].map(i => (
              <Col key={i} md={6} lg={3}>
                <div className="news-skeleton">
                  <div className="news-skeleton-image" />
                  <div className="news-skeleton-content">
                    <div className="news-skeleton-line" />
                    <div className="news-skeleton-line" />
                    <div className="news-skeleton-line short" />
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        ) : paginated.length > 0 ? (
          <>
            <Row className="g-4">
              {paginated.map(item => (
                <Col key={item.id} md={6} lg={3}>
                  <Card as={Link} to={`/noticias/${item.slug}`} className="news-card">
                    <div
                      className="news-card-img-wrapper"
                      style={{ backgroundImage: `url(${getNewsImage(item)})` }}
                      role="img"
                      aria-label={getNewsTitle(item)}
                    >
                      <span className="news-card-category">{getNewsCategoryName(item)}</span>
                    </div>
                    <Card.Body className="news-card-body">
                      <div className="news-card-date">{formatNewsDateShort(item.dataNoticia)}</div>
                      <Card.Title className="news-card-title">{getNewsTitle(item)}</Card.Title>
                      <span className="news-card-read-more">
                        Ler mais <span className="arrow">→</span>
                      </span>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>

            {/* Paginação */}
            {totalPages > 1 && (
              <div className="news-pagination">
                <button
                  className="news-pagination-btn news-pagination-nav"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  aria-label="Página anterior"
                >
                  <i className="bi bi-chevron-left"></i>
                </button>

                {getPageNumbers().map((page, i) =>
                  page === '...' ? (
                    <span key={`dots-${i}`} className="news-pagination-dots">...</span>
                  ) : (
                    <button
                      key={page}
                      className={`news-pagination-btn${currentPage === page ? ' active' : ''}`}
                      onClick={() => goToPage(page as number)}
                    >
                      {page}
                    </button>
                  )
                )}

                <button
                  className="news-pagination-btn news-pagination-nav"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  aria-label="Próxima página"
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-5">
            <i className="bi bi-inbox" style={{ fontSize: '4rem', color: 'var(--color-gray)' }}></i>
            <h3 className="mt-3">Nenhuma notícia encontrada</h3>
            <p>Tente ajustar sua pesquisa.</p>
          </div>
        )}
      </Container>
    </div>
  );
};

export default NewsListing;
